import { useState, useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import api from '../utils/api';
import { AdminAuthState } from '../types';

/**
 * Hook to check if the current user is an admin
 */
export const useAdminAuth = (): AdminAuthState => {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [adminState, setAdminState] = useState<AdminAuthState>({
    isAdmin: false,
    admin: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isSignedIn) {
        setAdminState({
          isAdmin: false,
          admin: null,
          loading: false,
          error: null
        });
        return;
      }

      try {
        // First check if the user has admin metadata in Clerk
        const hasAdminMetadata = !!(
          user?.publicMetadata?.role === 'admin' ||
          user?.publicMetadata?.isAdmin === true ||
          user?.unsafeMetadata?.role === 'admin' ||
          user?.unsafeMetadata?.isAdmin === true
        );

        if (hasAdminMetadata) {
          // If the user has admin metadata, set them as an admin immediately
          setAdminState({
            isAdmin: true,
            admin: {
              id: user.id,
              email: user.primaryEmailAddress?.emailAddress || '',
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
              role: 'admin',
              created_at: user.createdAt ? 
                (typeof user.createdAt === 'string' ? user.createdAt : new Date(user.createdAt).toISOString()) 
                : new Date().toISOString()
            },
            loading: false,
            error: null
          });
          return;
        }

        // If no metadata, fall back to API check
        const token = await getToken();
        const adminData = await api.admin.checkAdminStatus(token || undefined);
        
        setAdminState({
          isAdmin: !!adminData,
          admin: adminData,
          loading: false,
          error: null
        });
      } catch (error) {
        console.error("Error checking admin status:", error);
        setAdminState({
          isAdmin: false,
          admin: null,
          loading: false,
          error: "Failed to validate admin status"
        });
      }
    };

    checkAdminStatus();
  }, [isSignedIn, user, getToken]);

  return adminState;
};

export default useAdminAuth;
